import { OrdenItem } from '../../domain/orden.domain';
import { TarifaDetalle } from '../../domain/tarifa-detalle.domain';
import { TipoProducto } from '../../domain/tipo-producto.domain';
import { environment } from '../../../environments/environment';
import { PricingMatchRejection, PricingMatchTrace, PricingTrace } from './pricing-trace.model';

export interface TarifaDetalleMatchResult {
  matched?: TarifaDetalle;
  candidates: TarifaDetalle[];
  trace: PricingTrace;
}

/**
 * Convierte un valor de hora a minutos desde medianoche.
 * Soporta múltiples formatos:
 * - ISO strings (ej: "1970-01-01T07:00:00+00:00") - usa UTC para preservar el valor original
 * - Strings HH:mm o HH:mm:ss (nuevo formato desde API)
 * - Objetos Date (usa hora LOCAL porque vienen del DatePicker del formulario)
 * 
 * IMPORTANTE: 
 * - Los strings del API (ISO o HH:mm:ss) se parsean directamente.
 * - Los Date objects vienen del DatePicker y fueron creados con hora LOCAL,
 *   así que usamos getHours()/getMinutes() para extraer la hora correcta.
 * 
 * @returns minutos desde medianoche (0-1439) o null si no se puede parsear
 */
export function timeToMinutes(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  // Caso: Date object (viene del DatePicker del form, creado con hora LOCAL)
  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return null;
    }
    // Siempre usar hora LOCAL porque el DatePicker crea Dates con hora local
    // Ej: new Date(1970, 0, 1, 7, 0, 0) representa 07:00 local
    const hours = value.getHours();
    const minutes = value.getMinutes();
    return hours * 60 + minutes;
  }

  // Caso: String
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    // Caso ISO con 'T' (ej: "1970-01-01T07:00:00+00:00") - formato legacy del API
    if (trimmed.includes('T')) {
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) {
        return null;
      }
      // Usar UTC para ISO strings porque el API envía horas en UTC
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      return hours * 60 + minutes;
    }

    // Caso HH:mm o HH:mm:ss (ej: "07:00", "07:00:00") - nuevo formato del API
    const timeRegex = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
    const match = trimmed.match(timeRegex);
    if (match) {
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return hours * 60 + minutes;
      }
    }
  }

  return null;
}

export class TarifaDetalleMatchEngine {
  match(item: Partial<OrdenItem>, detalles: TarifaDetalle[], ctxTarifa?: { id?: number; codigo?: string; nombre?: string }): TarifaDetalleMatchResult {
    const tipoProducto = this.asTipoProducto(item.tipoProducto);
    const unidadTarifacion = tipoProducto?.unidadTarifacion ?? null;
    const debugLog = !environment.production;
    const itemRef = item.id ?? item.ordenIndex ?? '-';
    const trace: PricingMatchTrace = {
      inputs: {
        tipoProductoId: this.extractId(tipoProducto),
        tipoProductoCodigo: tipoProducto?.codigo ?? null,
        unidadTarifacion,
        diaTipo: item.diaTipo ?? null,
        horaDesde: item.horaDesde ?? null,
        horaHasta: item.horaHasta ?? null,
        frecuencia: tipoProducto?.codigo === 'AIRE_SPOT' ? (item.frecuencia ?? null) : null,
        programaId: this.extractId(item.programa),
        materialId: this.extractId(item.material),
        tipoMaterialId: this.extractTipoMaterialId(item.material),
        formatoDigitalId: this.extractId(item.formatoDigital),
        ubicacionDigitalId: this.extractId(item.ubicacionDigital),
        plataformaRedes: item.plataformaRedes ?? null,
        fechaDesde: item.fechaDesde ?? null,
        fechaHasta: item.fechaHasta ?? null
      },
      candidates: [],
      rejections: [],
      selectedDetalle: null,
      baseCandidatesIds: [],
      acceptedCandidatesIds: [],
      rejectionsByDetalleId: {}
    };

    if (!tipoProducto || !unidadTarifacion) {
      return {
        matched: undefined,
        candidates: [],
        trace: {
          status: 'INCOMPLETE',
          message: 'Tipo de producto incompleto para tarifar.',
          tarifa: ctxTarifa ?? undefined,
          match: trace
        }
      };
    }

    const tipoProductoId = this.extractId(tipoProducto);
    const baseCandidates = detalles.filter((detalle) => {
      const detalleTipoProductoId = this.extractId(detalle.tipoProducto);
      return detalleTipoProductoId === tipoProductoId
        && String(detalle.unidadTarifacion || '').toUpperCase() === String(unidadTarifacion || '').toUpperCase()
        && this.isActive((detalle as any).active);
    });
    trace.baseCandidatesIds = baseCandidates
      .map((detalle) => detalle.id ?? null)
      .filter((id): id is number => id !== null);

    if (debugLog) {
      // LOG: Inicio del proceso de matching (FRONTEND)
      console.group(`[TARIFA_DETALLE_MATCH_FRONTEND] Inicio matching (${tipoProducto?.codigo ?? 'N/A'} #${itemRef})`);
      console.log('Inputs:', {
        tarifa_id: ctxTarifa?.id,
        tarifa_codigo: ctxTarifa?.codigo,
        tipo_producto_id: tipoProductoId,
        unidad_tarifacion: unidadTarifacion,
        item_data: {
          dia_tipo: item.diaTipo,
          hora_desde: item.horaDesde,
          hora_hasta: item.horaHasta,
          frecuencia: tipoProducto?.codigo === 'AIRE_SPOT' ? (item.frecuencia ?? null) : null,
          programa_id: this.extractId(item.programa),
          material_id: this.extractId(item.material),
          tipo_material_id: this.extractTipoMaterialId(item.material),
          formato_digital_id: this.extractId(item.formatoDigital),
          ubicacion_digital_id: this.extractId(item.ubicacionDigital),
          plataforma_redes: item.plataformaRedes,
        },
        candidatos_base_count: baseCandidates.length,
        total_detalles_tarifa: detalles.length,
        base_candidates_ids: trace.baseCandidatesIds
      });
      console.groupEnd();
    }

    const { matched, rejections, rejectionsByDetalleId } = this.evaluateCandidates(baseCandidates, item);
    trace.rejections = rejections;
    trace.acceptedCandidatesIds = matched
      .map((detalle) => detalle.id ?? null)
      .filter((id): id is number => id !== null);
    trace.rejectionsByDetalleId = rejectionsByDetalleId;

    if (debugLog) {
      // LOG: Resultados de evaluación
      console.log(`[TARIFA_DETALLE_MATCH_FRONTEND] Evaluación completada (${tipoProducto?.codigo ?? 'N/A'} #${itemRef})`, {
        matches_count: matched.length,
        rejections_count: rejections.length,
        accepted_candidates_ids: trace.acceptedCandidatesIds,
        rejections: rejections.map(r => ({
          detalle_id: r.detalleId,
          reasons: r.reasons,
          prioridad: r.prioridad,
        })),
        rejections_by_detalle_id: trace.rejectionsByDetalleId
      });
    }

    if (matched.length === 0) {
      const primaryReason = rejections[0]?.reasons?.[0];
      const message = baseCandidates.length === 0
        ? 'No hay tarifa detalle para el tipo/unidad.'
        : primaryReason ? `Sin match por ${primaryReason}.` : 'No se encontró TarifaDetalle aplicable.';
      return {
        matched: undefined,
        candidates: [],
        trace: {
          status: 'NO_MATCH',
          message,
          tarifa: ctxTarifa ?? undefined,
          match: trace
        }
      };
    }

    /**
     * ORDENAMIENTO CRÍTICO - Debe ser idéntico al backend (TarifaDetalleResolver)
     * 
     * Orden de prioridad (descendente = mejor primero):
     * 1. Prioridad (mayor = mejor) - Campo numérico explícito de la tarifa
     * 2. Specificity Score (mayor = mejor) - Calculado según criterios específicos:
     *    - diaTipo (+1 si tiene)
     *    - horaDesde/horaHasta (+1 si tiene)
     *    - programa (+1 si tiene)
     *    - tipoMaterial (+1 si tiene)
     *    - formatoDigital (+1 si tiene)
     *    - ubicacionDigital (+1 si tiene)
     *    - plataformaRedes (+1 si tiene)
     * 3. ID (menor = mejor) - Desempate determinístico
     * 
     * IMPORTANTE: Este ordenamiento debe ser EXACTAMENTE igual al backend
     * para garantizar que ambos seleccionen la misma tarifa_detalle.
     */
    const scored = matched.map((detalle) => ({
      detalle,
      specificityScore: this.calculateSpecificityScore(detalle)
    })).sort((a, b) => {
      // 1. Prioridad (mayor primero)
      const prioridadA = a.detalle.prioridad ?? 0;
      const prioridadB = b.detalle.prioridad ?? 0;
      if (prioridadB !== prioridadA) {
        return prioridadB - prioridadA; // DESC: mayor prioridad primero
      }
      
      // 2. Specificity Score (mayor primero)
      if (b.specificityScore !== a.specificityScore) {
        return b.specificityScore - a.specificityScore; // DESC: mayor especificidad primero
      }
      
      // 3. ID (menor primero) - Desempate determinístico
      const idA = a.detalle.id ?? 0;
      const idB = b.detalle.id ?? 0;
      return idA - idB; // ASC: menor ID primero
    });

    const selected = scored[0];

    trace.candidates = scored.map((entry, index) => ({
      id: entry.detalle.id ?? null,
      prioridad: entry.detalle.prioridad ?? null,
      precioUnitario: this.parsePrecio(entry.detalle.precioUnitario),
      unidadTarifacion: entry.detalle.unidadTarifacion ?? null,
      specificity: entry.specificityScore,
      rank: index + 1
    }));

    trace.selectedDetalle = {
      id: selected.detalle.id ?? null,
      prioridad: selected.detalle.prioridad ?? null,
      precioUnitario: this.parsePrecio(selected.detalle.precioUnitario),
      unidadTarifacion: selected.detalle.unidadTarifacion ?? null,
      specificity: selected.specificityScore,
      rank: 1
    };

    if (debugLog) {
      // LOG: Ordenamiento y selección final (FRONTEND)
      console.group(`[TARIFA_DETALLE_MATCH_FRONTEND] Ordenamiento y selección (${tipoProducto?.codigo ?? 'N/A'} #${itemRef})`);
      console.log('Candidatos ordenados:', scored.map((entry, index) => ({
        rank: index + 1,
        id: entry.detalle.id,
        prioridad: entry.detalle.prioridad ?? 0,
        specificity: entry.specificityScore,
        precio_unitario: this.parsePrecio(entry.detalle.precioUnitario),
        hora_desde_raw: entry.detalle.horaDesde,
        hora_hasta_raw: entry.detalle.horaHasta,
        hora_desde_minutes: timeToMinutes(entry.detalle.horaDesde),
        hora_hasta_minutes: timeToMinutes(entry.detalle.horaHasta),
      })));
      console.log('Tarifa Detalle Seleccionada:', {
        id: selected.detalle.id,
        prioridad: selected.detalle.prioridad ?? 0,
        specificity: selected.specificityScore,
        precio_unitario: this.parsePrecio(selected.detalle.precioUnitario),
        unidad_tarifacion: selected.detalle.unidadTarifacion,
        hora_desde_minutes: timeToMinutes(selected.detalle.horaDesde),
        hora_hasta_minutes: timeToMinutes(selected.detalle.horaHasta),
      });
      
      // Comparar con el segundo candidato si existe
      if (scored.length > 1) {
        const second = scored[1];
        const razon = this.getSelectionReason(selected, second);
        console.log('Razón de selección:', razon);
        console.log('Comparación con segundo candidato:', {
          seleccionada: {
            id: selected.detalle.id,
            prioridad: selected.detalle.prioridad ?? 0,
            specificity: selected.specificityScore,
            hora_desde_minutes: timeToMinutes(selected.detalle.horaDesde),
            hora_hasta_minutes: timeToMinutes(selected.detalle.horaHasta),
          },
          segunda: {
            id: second.detalle.id,
            prioridad: second.detalle.prioridad ?? 0,
            specificity: second.specificityScore,
            hora_desde_minutes: timeToMinutes(second.detalle.horaDesde),
            hora_hasta_minutes: timeToMinutes(second.detalle.horaHasta),
          },
        });
      }
      console.groupEnd();
    }

    return {
      matched: selected.detalle,
      candidates: scored.map((entry) => entry.detalle),
      trace: {
        status: 'MATCHED',
        message: null,
        tarifa: ctxTarifa ?? undefined,
        match: trace,
        matchedDetalle: selected.detalle
      }
    };
  }

  private evaluateCandidates(
    candidates: TarifaDetalle[],
    item: Partial<OrdenItem>
  ): { matched: TarifaDetalle[]; rejections: PricingMatchRejection[]; rejectionsByDetalleId: Record<string, { reasons: string[]; detailSnapshot?: Record<string, any> }> } {
    const matched: TarifaDetalle[] = [];
    const rejections: PricingMatchRejection[] = [];
    const rejectionsByDetalleId: Record<string, { reasons: string[]; detailSnapshot?: Record<string, any> }> = {};

    candidates.forEach((detalle) => {
      const reasons = this.evaluateDetalle(detalle, item);
      if (reasons.length > 0) {
        const detalleId = detalle.id ?? null;
        rejections.push({
          detalleId,
          reasons,
          prioridad: detalle.prioridad ?? null,
          unidadTarifacion: detalle.unidadTarifacion ?? null,
          precioUnitario: this.parsePrecio(detalle.precioUnitario)
        });
        if (detalleId !== null) {
          rejectionsByDetalleId[String(detalleId)] = {
            reasons,
            detailSnapshot: this.buildDetalleSnapshot(detalle)
          };
        }
      } else {
        matched.push(detalle);
      }
    });

    return { matched, rejections, rejectionsByDetalleId };
  }

  private evaluateDetalle(detalle: TarifaDetalle, item: Partial<OrdenItem>): string[] {
    const reasons: string[] = [];
    if (detalle.diaTipo !== null && detalle.diaTipo !== undefined) {
      if (!item.diaTipo || detalle.diaTipo !== item.diaTipo) {
        reasons.push('diaTipo');
      }
    }

    // Evaluar rango de horas usando la semántica del backend
    const timeMatchResult = this.matchesTimeRange(detalle, item);
    if (!timeMatchResult.matches) {
      reasons.push('hora');
      // LOG detallado para diagnóstico de horas
      if (!environment.production) {
        console.log('[TARIFA_DETALLE_MATCH_FRONTEND] Rechazo por hora', {
          detalle_id: detalle.id,
          detalle_hora_desde_raw: detalle.horaDesde,
          detalle_hora_hasta_raw: detalle.horaHasta,
          item_hora_desde_raw: item.horaDesde,
          item_hora_hasta_raw: item.horaHasta,
          detalle_desde_minutes: timeMatchResult.detalleDesdeMinutes,
          detalle_hasta_minutes: timeMatchResult.detalleHastaMinutes,
          item_desde_minutes: timeMatchResult.itemDesdeMinutes,
          item_hasta_minutes: timeMatchResult.itemHastaMinutes,
          reason: timeMatchResult.reason
        });
      }
    }

    if (detalle.programa && !this.matchesEntity(detalle.programa, item.programa)) {
      reasons.push('programa');
    }

    if (detalle.tipoMaterial && !this.matchesTipoMaterial(detalle.tipoMaterial, item.material)) {
      reasons.push('tipoMaterial');
    }

    if (detalle.formatoDigital && !this.matchesEntity(detalle.formatoDigital, item.formatoDigital)) {
      reasons.push('formatoDigital');
    }

    if (detalle.ubicacionDigital && !this.matchesEntity(detalle.ubicacionDigital, item.ubicacionDigital)) {
      reasons.push('ubicacionDigital');
    }

    // Corregido: verificar explícitamente null/undefined para evitar falsy (ej: '', 0)
    if (detalle.plataformaRedes !== null && detalle.plataformaRedes !== undefined && detalle.plataformaRedes !== item.plataformaRedes) {
      reasons.push('plataformaRedes');
    }

    return reasons;
  }

  private matchesEntity(expected: any, actual: any): boolean {
    if (expected === null || expected === undefined) {
      return true;
    }
    if (actual === null || actual === undefined) {
      return false;
    }
    const expectedId = this.extractId(expected);
    const actualId = this.extractId(actual);
    if (expectedId !== null && actualId !== null) {
      return expectedId === actualId;
    }

    const expectedCodigo = this.extractCodigo(expected);
    const actualCodigo = this.extractCodigo(actual);
    if (expectedCodigo && actualCodigo) {
      return expectedCodigo === actualCodigo;
    }

    if (typeof expected === 'string' && typeof actual === 'string') {
      return expected.trim().toUpperCase() === actual.trim().toUpperCase();
    }

    return false;
  }

  private matchesTipoMaterial(expected: any, material: any): boolean {
    if (expected === null || expected === undefined) {
      return true;
    }
    if (!material) {
      return false;
    }
    const materialTipoId = this.extractTipoMaterialId(material);
    return materialTipoId !== null && this.extractId(expected) === materialTipoId;
  }

  /**
   * Evalúa si el item está dentro del rango de horas del detalle.
   * 
   * SEMÁNTICA IDÉNTICA AL BACKEND (TarifaDetalleResolver::matchesTimeRange):
   * - Si detalleDesde y detalleHasta son null => wildcard (match siempre)
   * - Si itemDesde o itemHasta son null => no match
   * - Si detalleDesde existe y itemDesde < detalleDesde => no match
   * - Si detalleHasta existe y itemHasta > detalleHasta => no match
   * - Soporta rangos abiertos (solo desde o solo hasta)
   */
  private matchesTimeRange(detalle: TarifaDetalle, item: Partial<OrdenItem>): {
    matches: boolean;
    reason?: string;
    detalleDesdeMinutes: number | null;
    detalleHastaMinutes: number | null;
    itemDesdeMinutes: number | null;
    itemHastaMinutes: number | null;
  } {
    const detalleDesdeMinutes = timeToMinutes(detalle.horaDesde);
    const detalleHastaMinutes = timeToMinutes(detalle.horaHasta);
    const itemDesdeMinutes = timeToMinutes(item.horaDesde);
    const itemHastaMinutes = timeToMinutes(item.horaHasta);

    const result = {
      detalleDesdeMinutes,
      detalleHastaMinutes,
      itemDesdeMinutes,
      itemHastaMinutes
    };

    // Si el detalle no tiene horas definidas => wildcard, match siempre
    if (detalleDesdeMinutes === null && detalleHastaMinutes === null) {
      return { matches: true, reason: 'wildcard (detalle sin horas)', ...result };
    }

    // Si el item no tiene horas => no match
    if (itemDesdeMinutes === null || itemHastaMinutes === null) {
      return { matches: false, reason: 'item sin hora desde/hasta', ...result };
    }

    // Verificar límite inferior (si existe en detalle)
    if (detalleDesdeMinutes !== null && itemDesdeMinutes < detalleDesdeMinutes) {
      return { matches: false, reason: `itemDesde (${itemDesdeMinutes}) < detalleDesde (${detalleDesdeMinutes})`, ...result };
    }

    // Verificar límite superior (si existe en detalle)
    if (detalleHastaMinutes !== null && itemHastaMinutes > detalleHastaMinutes) {
      return { matches: false, reason: `itemHasta (${itemHastaMinutes}) > detalleHasta (${detalleHastaMinutes})`, ...result };
    }

    return { matches: true, reason: 'rango válido', ...result };
  }

  private calculateSpecificityScore(detalle: TarifaDetalle): number {
    let score = 0;
    if (detalle.diaTipo) score++;
    if (detalle.horaDesde || detalle.horaHasta) score++;
    if (detalle.programa) score++;
    if (detalle.tipoMaterial) score++;
    if (detalle.formatoDigital) score++;
    if (detalle.ubicacionDigital) score++;
    if (detalle.plataformaRedes) score++;
    return score;
  }

  private extractId(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object' && value.id) {
      return Number(value.id);
    }
    if (typeof value === 'object' && value['@id']) {
      const match = String(value['@id']).match(/\/(\d+)$/);
      return match ? Number(match[1]) : null;
    }
    if (typeof value === 'string') {
      const match = value.match(/\/(\d+)$/);
      return match ? Number(match[1]) : null;
    }
    return null;
  }

  private extractTipoMaterialId(material: any): number | null {
    if (!material) {
      return null;
    }
    if (typeof material === 'object' && material.tipoMaterial) {
      return this.extractId(material.tipoMaterial);
    }
    return null;
  }


  private parsePrecio(value: string | number | undefined): number {
    if (value === undefined || value === null) {
      return 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    return parseFloat(value) || 0;
  }

  private asTipoProducto(value: unknown): TipoProducto | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value as TipoProducto;
  }

  /**
   * Obtiene la razón por la cual se seleccionó un detalle sobre otro
   */
  private getSelectionReason(selected: { detalle: TarifaDetalle; specificityScore: number }, second: { detalle: TarifaDetalle; specificityScore: number }): string {
    const prioridadSelected = selected.detalle.prioridad ?? 0;
    const prioridadSecond = second.detalle.prioridad ?? 0;
    
    if (prioridadSelected > prioridadSecond) {
      return `Mayor prioridad (${prioridadSelected} vs ${prioridadSecond})`;
    }
    
    if (selected.specificityScore > second.specificityScore) {
      return `Mayor especificidad (${selected.specificityScore} vs ${second.specificityScore})`;
    }
    
    const idSelected = selected.detalle.id ?? 0;
    const idSecond = second.detalle.id ?? 0;
    return `Menor ID (${idSelected} vs ${idSecond})`;
  }

  private buildDetalleSnapshot(detalle: TarifaDetalle): Record<string, any> {
    return {
      id: detalle.id ?? null,
      prioridad: detalle.prioridad ?? null,
      unidadTarifacion: detalle.unidadTarifacion ?? null,
      diaTipo: detalle.diaTipo ?? null,
      horaDesde: detalle.horaDesde ?? null,
      horaHasta: detalle.horaHasta ?? null,
      programaId: this.extractId((detalle as any).programa),
      tipoMaterialId: this.extractId((detalle as any).tipoMaterial),
      formatoDigitalId: this.extractId(detalle.formatoDigital),
      formatoDigitalCodigo: this.extractCodigo(detalle.formatoDigital),
      ubicacionDigitalId: this.extractId(detalle.ubicacionDigital),
      ubicacionDigitalCodigo: this.extractCodigo(detalle.ubicacionDigital),
      plataformaRedes: detalle.plataformaRedes ?? null,
      active: (detalle as any).active ?? null
    };
  }

  private extractCodigo(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed.toUpperCase() : null;
    }
    if (typeof value === 'object') {
      const codigo = value.codigo || value.code || value.slug;
      if (typeof codigo === 'string' && codigo.trim()) {
        return codigo.trim().toUpperCase();
      }
    }
    return null;
  }
  private isActive(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      return value === '1' || value.toLowerCase() === 'true';
    }
    return !!value;
  }
}
